import React from 'react';
import { useFormContext } from 'react-hook-form';
import { getLkKind } from './utils';
import {
  linkKinds2plus0,
  linkKinds2plus2,
  linkKinds4plus0,
  linkKindsMultiband,
  linkKindsMultiband2plus0,
} from './link-kind-utils';
import {
  MAIN_MAIN,
  MAIN_DIVERSE,
  DIVERSE_MAIN,
  DIVERSE_DIVERSE,
  CARRIER3,
} from 'src/components/profilechart/ProfileEditorModal';

// link kinds whose paths are referred to as Link A and Link B
const linkKindsAB = [
  ...linkKindsMultiband,
  ...linkKinds2plus0,
  ...linkKinds2plus2,
];

// Link kinds with multiple Component Carriers
const linkKindsCC = ['carrier_1_cc_2', 'carrier_2_cc_2'];
const linkKindsCC3 = ['carrier_2_cc_3'];

// link kinds whose paths are referred to as Primary to Primary and
// Secondary to Secondary
const linkKindsPS = ['unlicensed_1plus1'];

// link kinds whose paths are referred to as:
// - Primary to Primary
// - Primary to Secondary
// - Secondary to Primary
// - Secondary to Secondary
const linkKindsPPSS = ['hot_standby', 'unit_redundancy'];

// link kinds whose paths are referred to as Link A - D
const linkKindsABCD = linkKinds4plus0;

const allPathNames = {
  cc: ['Component Carrier 1', 'Component Carrier 2'],
  cc3: ['Component Carrier 1', 'Component Carrier 2', 'Component Carrier 3'],
  ab: ['Link A', 'Link B'],
  abcd: ['Link A', 'Link B', 'Link C', 'Link D'],
  // Multiband 2+0: Link B is a 2+0 so its ends are Link B1/Link B2.
  // Link B itself has no end panel (like the aggregate), so it is absent here.
  multiband2plus0: ['Link A', 'Link B1', 'Link B2'],
  ps: ['Primary to Primary', 'Secondary to Secondary'],
  ppss: [
    'Primary to Primary',
    'Primary to Secondary',
    'Secondary to Primary',
    'Secondary to Secondary',
  ],
};

function getSummaryLabels(lk: string, pathNames: string[]) {
  if (linkKindsMultiband2plus0.includes(lk)) {
    return ['Aggregate', 'Link A', 'Link B Aggregate', 'Link B1', 'Link B2'];
  }
  if (pathNames === allPathNames.ab || pathNames === allPathNames.abcd) {
    return ['Aggregate', ...pathNames];
  } else {
    return [...pathNames];
  }
}

function getLicensedHotStandbyDiverseHeights(lk, getValues) {
  if (lk !== 'hot_standby') {
    return null;
  }

  // "lh": local height, "rh": remote height
  // "la": local antenna, "ra": remote antennas
  const [laProt, raProt, lhPrimary, lhSecondary, rhPrimary, rhSecondary] =
    getValues([
      'local.radios.0.antennas.0.config.antenna_protection',
      'remote.radios.0.antennas.0.config.antenna_protection',
      'local.radios.0.antennas.0.height',
      'local.radios.2.antennas.0.height',
      'remote.radios.0.antennas.0.height',
      'remote.radios.1.antennas.0.height',
    ]);

  const isDiverse =
    laProt === 'Spatial Diversity' || raProt === 'Spatial Diversity';

  const getHeights = (prot, heightPrimary, heightSecondary) => {
    if (prot === 'Spatial Diversity') {
      return [heightPrimary, heightSecondary];
    } else {
      return [heightPrimary, heightPrimary];
    }
  };

  if (isDiverse) {
    return [
      getHeights(laProt, lhPrimary, lhSecondary),
      getHeights(raProt, rhPrimary, rhSecondary),
    ];
  }

  return null;
}

function getPTP700DiverseHeights(getValues, isComplex) {
  if (!isComplex) {
    return null;
  }

  const product = getValues('local.radios.0.equipment.product');
  const isPtp700 =
    product === 'PTP 700 C+I' ||
    product === 'PTP 700 BSA' ||
    product === 'PTP 700 C-Only' ||
    product === 'PTP 700';

  if (!isPtp700) {
    return null;
  }

  // "lh": local height, "rh": remote height
  // "la": local antenna, "ra": remote antennas
  const [laProt, raProt, lhLinkA, lhLinkB, rhLinkA, rhLinkB] = getValues([
    'local.radios.0.antennas.0.config.antenna_protection',
    'remote.radios.0.antennas.0.config.antenna_protection',
    'local.radios.0.antennas.0.height',
    'local.radios.1.antennas.0.height',
    'remote.radios.0.antennas.0.height',
    'remote.radios.1.antennas.0.height',
  ]);

  const isDiverse =
    laProt === 'Spatial Diversity' ||
    laProt === 'Redundant Antennas' ||
    raProt === 'Spatial Diversity' ||
    raProt === 'Redundant Antennas';

  const getHeights = (prot, heightLinkA, heightLinkB) => {
    if (prot === 'Spatial Diversity' || prot === 'Redundant Antennas') {
      return [heightLinkA, heightLinkB];
    } else {
      return [heightLinkA, heightLinkA];
    }
  };

  if (isDiverse) {
    return [
      getHeights(laProt, lhLinkA, lhLinkB),
      getHeights(raProt, rhLinkA, rhLinkB),
    ];
  }

  return null;
}

function ptp700DiverseProfileOptions(pathNames) {
  // NOTE only gets used if getPTP700DiverseHeights() returns non-null.
  // NOTE see ProfileEditorModal.tsx
  const mainMain = 0;
  const diverseDiverse = 3;

  return [
    { text: pathNames[0], key: 0, value: mainMain },
    { text: pathNames[1], key: 1, value: diverseDiverse },
  ];
}

export function useLinkKind() {
  const { getValues } = useFormContext();
  const [lk] = getValues(['local.radios.0.equipment.link_protection']);

  const isComplex = !(
    lk == null ||
    lk === 'no_protection' ||
    lk === 'carrier_1_cc_1' ||
    lk === 'sd_1plus0'
  );

  let pathNames = [];
  if (linkKindsMultiband2plus0.includes(lk)) {
    pathNames = allPathNames.multiband2plus0;
  } else if (linkKindsAB.includes(lk)) {
    pathNames = allPathNames.ab;
  } else if (linkKindsPS.includes(lk)) {
    pathNames = allPathNames.ps;
  } else if (linkKindsPPSS.includes(lk)) {
    pathNames = allPathNames.ppss;
  } else if (linkKindsABCD.includes(lk)) {
    pathNames = allPathNames.abcd;
  } else if (linkKindsCC.includes(lk)) {
    pathNames = allPathNames.cc;
  } else if (linkKindsCC3.includes(lk)) {
    pathNames = allPathNames.cc3;
  }

  let diverseHeights = null;
  let selectedPathOptions = null;

  if (['sd_2plus0', 'sd_2plus2', 'sd_1plus0'].includes(lk)) {
    const [localMain, localDiverse, remoteMain, remoteDiverse] = getValues([
      'local.radios.0.antennas.0.height',
      'local.radios.0.antennas.0.diverse_antenna.height',
      'remote.radios.0.antennas.0.height',
      'remote.radios.0.antennas.0.diverse_antenna.height',
    ]);
    diverseHeights = [
      [localMain, localDiverse],
      [remoteMain, remoteDiverse],
    ];
    selectedPathOptions = [
      { text: 'Main to Main', key: 0, value: MAIN_MAIN },
      { text: 'Main to Diverse', key: 1, value: MAIN_DIVERSE },
      { text: 'Diverse to Main', key: 2, value: DIVERSE_MAIN },
      { text: 'Diverse to Diverse', key: 3, value: DIVERSE_DIVERSE },
    ];
  } else if (lk === 'carrier_2_cc_2') {
    selectedPathOptions = [
      { text: 'Component Carrier 1', key: MAIN_MAIN, value: MAIN_MAIN },
      {
        text: 'Component Carrier 2',
        key: DIVERSE_DIVERSE,
        value: DIVERSE_DIVERSE,
      },
    ];
  } else if (lk === 'carrier_2_cc_3') {
    selectedPathOptions = [
      { text: 'Component Carrier 1', key: MAIN_MAIN, value: MAIN_MAIN },
      {
        text: 'Component Carrier 2',
        key: DIVERSE_DIVERSE,
        value: DIVERSE_DIVERSE,
      },
      {
        text: 'Component Carrier 3',
        key: 'CC3',
        value: CARRIER3,
      },
    ];
  }

  const ptp700DiverseHeights = getPTP700DiverseHeights(getValues, isComplex);
  if (ptp700DiverseHeights != null) {
    diverseHeights = ptp700DiverseHeights;
    selectedPathOptions = ptp700DiverseProfileOptions(pathNames);
  }

  const hotStandbyDiverseHeights = getLicensedHotStandbyDiverseHeights(
    lk,
    getValues
  );
  if (hotStandbyDiverseHeights != null) {
    diverseHeights = hotStandbyDiverseHeights;
    selectedPathOptions = [
      { text: 'Primary to Primary', key: MAIN_MAIN, value: MAIN_MAIN },
      { text: 'Primary to Secondary', key: MAIN_DIVERSE, value: MAIN_DIVERSE },
      { text: 'Secondary to Primary', key: DIVERSE_MAIN, value: DIVERSE_MAIN },
      {
        text: 'Secondary to Secondary',
        key: DIVERSE_DIVERSE,
        value: DIVERSE_DIVERSE,
      },
    ];
  }

  let endPanelNames;
  if (lk === 'hot_standby' || lk === 'unit_redundancy') {
    endPanelNames = {
      local: [
        ['Primary', 0],
        ['Secondary', 2],
      ],
      remote: [
        ['Primary', 0],
        ['Secondary', 1],
      ],
    };
  } else {
    endPanelNames = {
      local: pathNames.map((n, i) => [n, i]),
      remote: pathNames.map((n, i) => [n, i]),
    };
  }

  return {
    lk,
    kind: getLkKind(lk),
    summaryLabels: getSummaryLabels(lk, pathNames),
    pathNames,
    endPanelNames,
    isComplex,
    // Multiband and unlicensed 2+0 have a top Aggregate summary but no
    // aggregate performance details (multiband 2+0 keeps its Link B
    // Aggregate details tab - only the top Aggregate is summary-only).
    showAggregateDetails:
      lk !== 'unlicensed_2plus0' &&
      lk !== 'multi_1plus0' &&
      !linkKindsMultiband2plus0.includes(lk),
    diverseHeights,
    selectedPathOptions,
  };
}
